/**
 * Web Nova loader — the twin of `nova-loader.tsx`.
 *
 * `NovaIcon` is the flat ink silhouette with transparent eyes: the body is a
 * single `currentColor` path and the eyes are cut out through a luminance
 * mask, so whatever sits behind the loader shows through them — including
 * while they move. The status picks the eye choreography (see
 * `nova-loader-motion.ts`); the CSS in global.css drives it on the compositor.
 *
 * Motion pauses when the icon is scrolled out of view or the tab is hidden,
 * and reduced motion shows a still face. The icon is decorative; the label
 * beside it carries the accessible status.
 */
import { useEffect, useId, useRef, useState, type CSSProperties, type FC, type RefObject } from 'react';
import { NOVA_BODY_PATH, NOVA_EYES, NOVA_VIEWBOX, type NovaStatus } from './nova-loader-motion';

function useMotionPaused(ref: RefObject<Element | null>, paused: boolean) {
  const [visible, setVisible] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);

  useEffect(() => {
    const onVisibility = () => setPageVisible(!document.hidden);
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(([entry]) => setVisible(Boolean(entry?.isIntersecting)));
    if (ref.current) observer?.observe(ref.current);
    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [ref]);

  return paused || !visible || !pageVisible;
}

export const NovaIcon: FC<{
  status?: NovaStatus;
  size?: number;
  paused?: boolean;
  className?: string;
  style?: CSSProperties;
  /**
   * 0..1 mic amplitude, published to CSS as `--nova-level`. Only the
   * `listening` status reads it. The browser's speech engines return text
   * without amplitude, so web dictation leaves it undefined and gets the
   * keyframes alone.
   */
  level?: number;
}> = ({ status = 'thinking', size = 20, paused = false, className, style, level }) => {
  const maskId = `nova-eyes-${useId().replace(/:/g, '')}`;
  const ref = useRef<SVGSVGElement>(null);
  const motionPaused = useMotionPaused(ref, paused);
  const { x, y, width, height } = NOVA_VIEWBOX;

  const amplitude = level == null ? undefined : Math.max(0, Math.min(1, level));

  return (
    <svg
      ref={ref}
      className={['nova-loader__icon', className].filter(Boolean).join(' ')}
      style={amplitude == null ? style : { ...style, ['--nova-level' as string]: String(amplitude) }}
      width={size}
      height={size}
      viewBox={`${x} ${y} ${width} ${height}`}
      aria-hidden="true"
      focusable="false"
      data-status={status}
      data-paused={motionPaused ? 'true' : undefined}
    >
      <defs>
        <mask
          id={maskId}
          x={x}
          y={y}
          width={width}
          height={height}
          maskUnits="userSpaceOnUse"
          maskContentUnits="userSpaceOnUse"
          style={{ maskType: 'luminance' }}
        >
          <rect x={x} y={y} width={width} height={height} fill="white" />
          <ellipse
            className="nova-loader__eye nova-loader__eye--left"
            cx={NOVA_EYES.left.cx}
            cy={NOVA_EYES.left.cy}
            rx={NOVA_EYES.rx}
            ry={NOVA_EYES.ry}
            fill="black"
          />
          <ellipse
            className="nova-loader__eye nova-loader__eye--right"
            cx={NOVA_EYES.right.cx}
            cy={NOVA_EYES.right.cy}
            rx={NOVA_EYES.rx}
            ry={NOVA_EYES.ry}
            fill="black"
          />
        </mask>
      </defs>
      <path className="nova-loader__body" d={NOVA_BODY_PATH} fill="currentColor" mask={`url(#${maskId})`} />
    </svg>
  );
};

export default NovaIcon;
